import { categoryIconMap } from '../components/PPEIcons';

// Tipos para las categorías de EPP
export type PPECategoryType = 
  'craneana' | 
  'ocular' | 
  'facial' | 
  'respiratoria' | 
  'indumentaria' | 
  'manos' | 
  'calzado' | 
  'auditiva' | 
  'altura';

// Interfaz para subcategorías de EPP
export interface PPESubcategory {
  id: string;
  name: string;
  description: string;
  standard?: string;
}

// Interfaz para categorías de EPP
export interface PPECategory {
  id: PPECategoryType;
  name: string;
  icon: any;
  description: string;
  subcategories: PPESubcategory[];
}

// Categorías de EPP y Protección Personal
export const ppeCategories: PPECategory[] = [
  {
    id: 'craneana',
    name: 'Protección Craneana',
    icon: categoryIconMap['craneana'],
    description: 'Protección para la cabeza contra impactos y riesgos eléctricos',
    subcategories: [
      {
        id: 'craneana-cascos',
        name: 'Cascos',
        description: 'Cascos de seguridad industriales y dieléctricos',
        standard: 'ANSI Z89.1 / IRAM 3620'
      },
      {
        id: 'craneana-accesorios',
        name: 'Accesorios',
        description: 'Mentoneras, arneses interiores, vinchas y repuestos',
        standard: 'EN 397'
      }
    ]
  },
  {
    id: 'ocular',
    name: 'Protección Ocular',
    icon: categoryIconMap['ocular'],
    description: 'Protección para los ojos contra partículas, salpicaduras y radiación',
    subcategories: [
      {
        id: 'ocular-anteojos',
        name: 'Anteojos',
        description: 'Protección contra impactos, partículas y radiación UV',
        standard: 'ANSI Z87.1 / IRAM 3630'
      },
      {
        id: 'ocular-antiparras',
        name: 'Antiparras',
        description: 'Protección hermética contra salpicaduras químicas y polvos',
        standard: 'EN 166'
      },
      {
        id: 'ocular-recetados',
        name: 'Recetados',
        description: 'Anteojos de seguridad con graduación personalizada',
        standard: 'ANSI Z87.1'
      }
    ]
  },
  {
    id: 'facial',
    name: 'Protección Facial',
    icon: categoryIconMap['facial'],
    description: 'Protección completa para el rostro contra diversos riesgos',
    subcategories: [
      {
        id: 'facial-protectores',
        name: 'Protectores Faciales',
        description: 'Pantallas faciales planas, burbuja y para alta temperatura',
        standard: 'ANSI Z87.1'
      },
      {
        id: 'facial-accesorios',
        name: 'Accesorios',
        description: 'Cabezales, adaptadores para cascos y visores de repuesto',
        standard: 'EN 166'
      }
    ]
  },
  {
    id: 'respiratoria',
    name: 'Protección Respiratoria',
    icon: categoryIconMap['respiratoria'],
    description: 'Protección para las vías respiratorias contra partículas, gases y vapores',
    subcategories: [
      {
        id: 'respiratoria-desechables',
        name: 'Desechables',
        description: 'Mascarillas descartables N95, FFP2 y polvos molestos',
        standard: 'NIOSH N95 / IRAM 3646'
      },
      {
        id: 'respiratoria-reutilizables',
        name: 'Reutilizables',
        description: 'Semimáscaras y máscaras de cara completa con filtros reemplazables',
        standard: 'EN 140 / EN 136'
      },
      {
        id: 'respiratoria-autonomos',
        name: 'Equipos Autónomos',
        description: 'Equipos de respiración autónoma (ERA) y suministro de aire',
        standard: 'NFPA 1981 / EN 137'
      }
    ]
  },
  {
    id: 'manos',
    name: 'Protección de Manos',
    icon: categoryIconMap['manos'],
    description: 'Guantes de protección técnica para todo tipo de riesgos',
    subcategories: [
      {
        id: 'manos-descartables',
        name: 'Descartables',
        description: 'Guantes de un solo uso para higiene y contacto ligero',
        standard: 'EN 455'
      },
      {
        id: 'manos-latex',
        name: 'Látex',
        description: 'Guantes de látex natural para limpieza y uso general',
        standard: 'EN 374'
      },
      {
        id: 'manos-nitrilo',
        name: 'Nitrilo',
        description: 'Alta resistencia a químicos, aceites e hidrocarburos',
        standard: 'EN 374 / EN 388'
      },
      {
        id: 'manos-pvc',
        name: 'PVC',
        description: 'Protección para ácidos, bases y manipulación pesada',
        standard: 'EN 374'
      },
      {
        id: 'manos-anticorte',
        name: 'Anticorte',
        description: 'Tejidos con fibras de alta tenacidad (Kevlar, HPPE)',
        standard: 'EN 388 nivel D/F'
      },
      {
        id: 'manos-temperatura',
        name: 'Temperatura',
        description: 'Protección contra calor por contacto, frío extremo o chispas',
        standard: 'EN 407 / EN 511'
      },
      {
        id: 'manos-tejidos',
        name: 'Tejidos',
        description: 'Guantes de algodón, mota y soporte textil para destreza',
        standard: 'EN 388'
      },
      {
        id: 'manos-dielectricos',
        name: 'Dieléctricos',
        description: 'Guantes de caucho aislante para trabajos con tensión eléctrica',
        standard: 'ASTM D120 / IEC 60903'
      }
    ]
  },
  {
    id: 'auditiva',
    name: 'Protección Auditiva',
    icon: categoryIconMap['auditiva'],
    description: 'Protección auditiva pasiva y activa para ambientes de alto decibel',
    subcategories: [
      {
        id: 'auditiva-copa',
        name: 'Copa',
        description: 'Protectores de copa con vincha o acoplables a casco',
        standard: 'ANSI S3.19 / IRAM 4060'
      },
      {
        id: 'auditiva-endoaural',
        name: 'Endoaural',
        description: 'Tapones auditivos expansibles de espuma o silicona con cordón',
        standard: 'ANSI S3.19'
      }
    ]
  },
  {
    id: 'indumentaria',
    name: 'Indumentaria',
    icon: categoryIconMap['indumentaria'],
    description: 'Ropa de trabajo y prendas de seguridad de alta durabilidad',
    subcategories: [
      {
        id: 'indumentaria-ropa-trabajo',
        name: 'Ropa de Trabajo',
        description: 'Pantalones, camisas, mamelucos y chaquetas de trabajo',
        standard: 'EN ISO 13688'
      },
      {
        id: 'indumentaria-abrigo',
        name: 'Abrigo',
        description: 'Camperas térmicas, buzos y chalecos acolchados',
        standard: 'EN 342'
      },
      {
        id: 'indumentaria-lluvia',
        name: 'Ropa para Lluvia',
        description: 'Trajes de agua, capas y pilotos impermeables',
        standard: 'EN 343'
      },
      {
        id: 'indumentaria-delantales',
        name: 'Delantales',
        description: 'Delantales de descarne, PVC y protección frontal',
        standard: 'EN ISO 13688'
      },
      {
        id: 'indumentaria-descartables',
        name: 'Descartables',
        description: 'Overoles y cofias tipo Tyvek para uso limitado',
        standard: 'EN 14126'
      },
      {
        id: 'indumentaria-quimica',
        name: 'Protección Química',
        description: 'Trajes encapsulados y prendas estancas a sustancias peligrosas',
        standard: 'EN 943'
      },
      {
        id: 'indumentaria-soldadura',
        name: 'Soldadura',
        description: 'Polainas, mangas y chaquetas de cuero descarne',
        standard: 'EN ISO 11611'
      },
      {
        id: 'indumentaria-ignifuga-arco',
        name: 'Ignífuga / Arco Eléctrico',
        description: 'Prendas ignífugas permanentes y protección contra arco eléctrico',
        standard: 'NFPA 2112 / NFPA 70E'
      }
    ]
  },
  {
    id: 'calzado',
    name: 'Calzado de Seguridad',
    icon: categoryIconMap['calzado'],
    description: 'Calzado técnico con puntera de seguridad y suelas antideslizantes',
    subcategories: [
      {
        id: 'calzado-zapatos',
        name: 'Zapatos',
        description: 'Zapatos bajos de seguridad con puntera de acero o composite',
        standard: 'IRAM 3610 / EN ISO 20345'
      },
      {
        id: 'calzado-botines',
        name: 'Botines',
        description: 'Botines de caña media con soporte para tobillo',
        standard: 'IRAM 3610'
      },
      {
        id: 'calzado-zapatillas',
        name: 'Zapatillas',
        description: 'Zapatillas de seguridad ultralivianas y deportivas',
        standard: 'EN ISO 20345'
      },
      {
        id: 'calzado-botas',
        name: 'Botas',
        description: 'Botas de caña alta de cuero o PVC para agua y químicos',
        standard: 'EN ISO 20345'
      },
      {
        id: 'calzado-visita',
        name: 'Calzado de Visita',
        description: 'Cubrecalzado y punteras desmontables para visitas a planta',
        standard: 'EN 12568'
      },
      {
        id: 'calzado-suecos',
        name: 'Suecos',
        description: 'Suecos higiénicos y antideslizantes para sanidad y alimentación',
        standard: 'EN ISO 20347'
      }
    ]
  },
  {
    id: 'altura',
    name: 'Trabajo en Altura',
    icon: categoryIconMap['altura'],
    description: 'Equipos certificados de prevención y detención de caídas',
    subcategories: [
      {
        id: 'altura-arneses',
        name: 'Arneses',
        description: 'Arneses integrales de cuerpo completo multipropósito y posicionamiento',
        standard: 'ANSI Z359.11 / IRAM 3622-1'
      },
      {
        id: 'altura-colas-amarre',
        name: 'Colas de Amarre',
        description: 'Cabos de vida simples y dobles con absorbedor de impacto',
        standard: 'ANSI Z359.13 / IRAM 3622-2'
      },
      {
        id: 'altura-retractiles',
        name: 'Retráctiles',
        description: 'Bloqueadores retráctiles automáticos de cinta y cable de acero',
        standard: 'ANSI Z359.14'
      },
      {
        id: 'altura-lineas-vida',
        name: 'Líneas de Vida',
        description: 'Sistemas de líneas de vida horizontales y verticales fijas/temporales',
        standard: 'EN 795'
      },
      {
        id: 'altura-accesorios',
        name: 'Accesorios',
        description: 'Mosquetones, conectores, puntos de anclaje y cintas antitrauma',
        standard: 'ANSI Z359.12'
      }
    ]
  }
];

// Función para obtener todas las subcategorías
export const getAllSubcategories = () => {
  const allSubcategories: (PPESubcategory & { categoryId: PPECategoryType, categoryName: string })[] = [];
  
  ppeCategories.forEach(category => {
    category.subcategories.forEach(subcategory => {
      allSubcategories.push({
        ...subcategory,
        categoryId: category.id,
        categoryName: category.name
      });
    });
  });
  
  return allSubcategories;
};